<template>
  <Transition name="modal-fade">
    <div v-if="show" class="edit-lead-contact-overlay" @click.self="closeModal">
      <div class="edit-lead-contact-modal">
        <!-- Modal Header -->
        <div class="edit-lead-contact-header">
          <div class="d-flex align-items-center gap-2">
            <div class="header-icon-badge">
              <i class="fa-solid fa-building-user text-info"></i>
            </div>
            <div>
              <h5 class="edit-lead-contact-title mb-0">Edit Recipient Contact</h5>
            </div>
          </div>
          <button type="button" class="btn-close-modal" @click="closeModal" :disabled="isSubmitting" aria-label="Close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="edit-lead-contact-body">
          <div v-if="isLoadingInitial" class="py-4 text-center">
            <i class="fa-solid fa-spinner fa-spin fa-2x text-info mb-2"></i>
            <div class="text-muted small">Loading contact details...</div>
          </div>

          <div v-else>
            <!-- 1. Manually input Recipient Checkbox Toggle -->
            <div class="d-flex align-items-center mb-3">
              <div class="form-check mb-0">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="manualRecipientCheck"
                  v-model="isManualRecipient"
                  @change="onManualToggleChange"
                  :disabled="isSubmitting"
                />
                <label class="form-check-label fw-semibold small text-dark user-select-none" for="manualRecipientCheck">
                  Manually input Recipient
                </label>
              </div>
            </div>

            <!-- 2. Existing Contact Person Picker (Dropdown) -->
            <div v-if="!isManualRecipient" class="mb-3">
              <label class="form-label fw-bold d-flex align-items-center justify-content-between mb-2">
                <span>Select from Organisation Contacts</span>
                <span v-if="isLoadingContacts" class="text-muted small fw-normal">
                  <i class="fa-solid fa-spinner fa-spin me-1"></i> Loading contacts...
                </span>
              </label>

              <VueMultiselect
                v-model="selectedContact"
                :options="orgContacts"
                :searchable="true"
                :close-on-select="true"
                :allow-empty="true"
                :show-labels="false"
                select-label=""
                deselect-label=""
                selected-label=""
                placeholder="Select an existing contact..."
                label="displayName"
                track-by="key"
                :disabled="isSubmitting || isLoadingContacts || orgContacts.length === 0"
                class="contact-multiselect"
              >
                <template #singleLabel="{ option }">
                  <div class="d-flex align-items-center gap-2 text-truncate">
                    <span class="fw-bold">{{ option.fullName }}</span>
                    <span v-if="option.email" class="text-muted small">({{ option.email }})</span>
                    <span
                      v-if="option.contact_type"
                      class="badge rounded-pill text-uppercase px-2 py-0.5 ms-1 contact-badge-item"
                      :class="getContactTypeBadgeClass(option.contact_type)"
                      style="font-size: 10px; font-weight: 700; letter-spacing: 0.3px;"
                    >
                      {{ option.contact_type }}
                    </span>
                    <span
                      v-if="option.is_primary && (!option.contact_type || option.contact_type.toLowerCase() !== 'primary')"
                      class="badge rounded-pill px-1.5 py-0.5 ms-1 contact-badge-item contact-type-badge-primary"
                      style="font-size: 9.5px; font-weight: 600;"
                    >
                      ★ Primary
                    </span>
                  </div>
                </template>
                <template #option="{ option }">
                  <div class="d-flex align-items-center justify-content-between w-100 py-0.5" style="gap: 12px;">
                    <div class="d-flex align-items-center gap-1 text-truncate" style="min-width: 0; flex: 1 1 auto;">
                      <span class="contact-option-name fw-bold">{{ option.fullName }}</span>
                      <span v-if="option.email" class="contact-option-sub text-muted small ms-1">({{ option.email }})</span>
                      <span v-if="option.role" class="contact-option-sub text-muted small ms-1">- {{ option.role }}</span>
                    </div>
                    <div class="d-flex align-items-center gap-1.5 flex-shrink-0" style="margin-left: auto;">
                      <span
                        v-if="option.contact_type"
                        class="badge rounded-pill text-uppercase px-2 py-0.5 contact-badge-item"
                        :class="getContactTypeBadgeClass(option.contact_type)"
                        style="font-size: 10px; font-weight: 700; letter-spacing: 0.3px;"
                      >
                        {{ option.contact_type }}
                      </span>
                      <span
                        v-if="option.is_primary && (!option.contact_type || option.contact_type.toLowerCase() !== 'primary')"
                        class="badge rounded-pill px-1.5 py-0.5 contact-badge-item contact-type-badge-primary"
                        style="font-size: 9.5px; font-weight: 600;"
                        title="Primary Contact"
                      >
                        ★ Primary
                      </span>
                    </div>
                  </div>
                </template>
                <template #noResult>No matching contacts found.</template>
                <template #noOptions>No contacts recorded for this organisation.</template>
              </VueMultiselect>
            </div>

            <!-- 3. Contact Input Fields (Direct Edit) -->
            <div class="row g-3">
              <!-- First Name -->
              <div class="col-sm-6">
                <div class="form-group mb-2">
                  <label class="form-label small fw-bold mb-2">
                    First Name <span class="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    v-model="form.recipient_first_name"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': validationErrors.firstName }"
                    placeholder="e.g. John"
                    :disabled="isSubmitting"
                    @input="onManualFieldInput('firstName')"
                  />
                  <div v-if="validationErrors.firstName" class="invalid-feedback d-block small">
                    {{ validationErrors.firstName }}
                  </div>
                </div>
              </div>

              <!-- Last Name -->
              <div class="col-sm-6">
                <div class="form-group mb-2">
                  <label class="form-label small fw-bold mb-2">
                    Last Name <span class="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    v-model="form.recipient_last_name"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': validationErrors.lastName }"
                    placeholder="e.g. Smith"
                    :disabled="isSubmitting"
                    @input="onManualFieldInput('lastName')"
                  />
                  <div v-if="validationErrors.lastName" class="invalid-feedback d-block small">
                    {{ validationErrors.lastName }}
                  </div>
                </div>
              </div>

              <!-- Email -->
              <div class="col-sm-7">
                <div class="form-group mb-2">
                  <label class="form-label small fw-bold mb-2">
                    Email Address <span class="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    v-model="form.recipient_email"
                    class="form-control form-control-sm"
                    :class="{ 'is-invalid': validationErrors.email }"
                    placeholder="e.g. john.smith@company.com"
                    :disabled="isSubmitting"
                    @input="onManualFieldInput('email')"
                  />
                  <div v-if="validationErrors.email" class="invalid-feedback d-block small">
                    {{ validationErrors.email }}
                  </div>
                </div>
              </div>

              <!-- Phone -->
              <div class="col-sm-5">
                <div class="form-group mb-2">
                  <label class="form-label small fw-bold mb-2">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    v-model="form.recipient_phone_number"
                    class="form-control form-control-sm"
                    placeholder="e.g. 0412 345 678"
                    :disabled="isSubmitting"
                    @input="onManualFieldInput()"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="edit-lead-contact-footer">
          <button
            type="button"
            class="btn btn-sm btn-light border px-3"
            @click="closeModal"
            :disabled="isSubmitting"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-sm btn-vertex-primary px-4 d-inline-flex align-items-center gap-2"
            @click="submitForm"
            :disabled="isSubmitting || isLoadingInitial"
          >
            <i v-if="isSubmitting" class="fa-solid fa-spinner fa-spin"></i>
            <span>{{ isSubmitting ? 'Saving Changes...' : 'Save Changes' }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import VueMultiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import '@/assets/scss/vue-multiselect.scss'

export default {
  name: 'EditLeadContactModal',

  components: {
    VueMultiselect,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    proposalId: {
      type: String,
      required: true,
    },
    currentLead: {
      type: Object,
      default: () => ({}),
    },
    currentProposal: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['close', 'updated'],

  data() {
    return {
      isSubmitting: false,
      isLoadingInitial: false,
      isLoadingContacts: false,
      isManualRecipient: false,
      orgContacts: [],
      selectedContact: null,
      form: {
        recipient_first_name: '',
        recipient_last_name: '',
        recipient_email: '',
        recipient_phone_number: '',
      },
      validationErrors: {
        firstName: '',
        lastName: '',
        email: '',
      },
    }
  },

  watch: {
    show(newVal) {
      if (newVal) {
        this.initializeModal()
      } else {
        this.resetState()
      }
    },
    selectedContact(newContact) {
      if (newContact) {
        this.form.recipient_first_name = (newContact.first_name || '').trim()
        this.form.recipient_last_name = (newContact.last_name || '').trim()
        this.form.recipient_email = (newContact.email || '').trim()
        this.form.recipient_phone_number = (newContact.phone_number || '').trim()
        this.clearValidationError('firstName')
        this.clearValidationError('lastName')
        this.clearValidationError('email')
      }
    },
  },

  methods: {
    async initializeModal() {
      this.isLoadingInitial = true
      this.isManualRecipient = false
      this.validationErrors = { firstName: '', lastName: '', email: '' }

      // 1. Populate initial form from currentLead / currentProposal
      const curLead = this.currentLead || {}
      const curProp = this.currentProposal || {}

      let firstName = curLead.first_name || ''
      let lastName = curLead.last_name || ''

      // Fallback: parse from client_lead_name if empty
      if (!firstName && curProp.client_lead_name) {
        const parts = curProp.client_lead_name.trim().split(' ')
        firstName = parts[0] || ''
        lastName = parts.slice(1).join(' ') || ''
      }

      this.form = {
        recipient_first_name: firstName,
        recipient_last_name: lastName || curLead.last_name || '',
        recipient_email: curLead.email || curProp.client_lead_email || '',
        recipient_phone_number: curLead.phone_number || '',
      }

      try {
        // 2. Resolve organisation UUID and fetch contacts
        const orgUuid =
          curLead.organisation_uuid ||
          curLead.organisation_id ||
          (curLead.organisation && (curLead.organisation.uuid || curLead.organisation.id)) ||
          curProp.organisation_uuid ||
          curProp.organisation_id ||
          ''

        const authOrgId =
          curLead.auth_organisation_id ||
          (curLead.organisation && curLead.organisation.auth_organisation_id) ||
          curProp.auth_organisation_id ||
          0

        await this.fetchContactsForOrganisation(orgUuid, authOrgId)
      } catch (err) {
        console.error('Failed to initialize edit modal:', err)
        this.$toast.error('Failed to load organisation contacts.')
      } finally {
        this.isLoadingInitial = false
      }
    },

    onManualToggleChange() {
      if (this.isManualRecipient) {
        this.selectedContact = null
        this.form = {
          recipient_first_name: '',
          recipient_last_name: '',
          recipient_email: '',
          recipient_phone_number: '',
        }
        this.validationErrors = { firstName: '', lastName: '', email: '' }
      } else {
        this.restoreInitialRecipient()
      }
    },

    restoreInitialRecipient() {
      const curLead = this.currentLead || {}
      const curProp = this.currentProposal || {}

      let firstName = curLead.first_name || ''
      let lastName = curLead.last_name || ''

      // Fallback: parse from client_lead_name if empty
      if (!firstName && curProp.client_lead_name) {
        const parts = curProp.client_lead_name.trim().split(' ')
        firstName = parts[0] || ''
        lastName = parts.slice(1).join(' ') || ''
      }

      this.form = {
        recipient_first_name: firstName,
        recipient_last_name: lastName || curLead.last_name || '',
        recipient_email: curLead.email || curProp.client_lead_email || '',
        recipient_phone_number: curLead.phone_number || '',
      }

      this.validationErrors = { firstName: '', lastName: '', email: '' }

      if (this.form.recipient_email && Array.isArray(this.orgContacts)) {
        const curEmail = this.form.recipient_email.trim().toLowerCase()
        const matched = this.orgContacts.find(
          (c) => (c.email || '').trim().toLowerCase() === curEmail
        )
        this.selectedContact = matched || null
      } else {
        this.selectedContact = null
      }
    },

    onManualFieldInput(field) {
      if (field) {
        this.clearValidationError(field)
      }
      if (this.selectedContact) {
        const contactFirst = (this.selectedContact.first_name || '').trim()
        const contactLast = (this.selectedContact.last_name || '').trim()
        const contactEmail = (this.selectedContact.email || '').trim()
        const contactPhone = (this.selectedContact.phone_number || '').trim()

        const formFirst = (this.form.recipient_first_name || '').trim()
        const formLast = (this.form.recipient_last_name || '').trim()
        const formEmail = (this.form.recipient_email || '').trim()
        const formPhone = (this.form.recipient_phone_number || '').trim()

        if (
          contactFirst !== formFirst ||
          contactLast !== formLast ||
          contactEmail !== formEmail ||
          contactPhone !== formPhone
        ) {
          this.selectedContact = null
        }
      }
    },

    async fetchContactsForOrganisation(orgUuid, authOrgId = 0) {
      this.orgContacts = []
      this.selectedContact = null
      this.isLoadingContacts = true

      try {
        let contacts = []

        if (orgUuid) {
          try {
            const res = await this.$axios.get(`/organisations/${orgUuid}/contacts`)
            if (res.data && res.data.contacts && res.data.contacts.length > 0) {
              contacts = res.data.contacts
            }
          } catch (e) {
            console.warn('Failed to fetch org contacts, trying auth users fallback:', e)
          }
        }

        if (contacts.length === 0 && authOrgId > 0) {
          try {
            const res = await this.$axios.get(`/auth/organisations/${authOrgId}/users`)
            if (res.data && res.data.users && res.data.users.length > 0) {
              contacts = res.data.users.map((u) => ({
                id: u.id,
                uuid: u.user_uuid || '',
                first_name: u.first_name || '',
                last_name: u.last_name || '',
                email: u.email || '',
                phone_number: '',
                role: 'Auth User',
                contact_type: 'user',
                is_primary: false,
              }))
            }
          } catch (e) {
            console.warn('Failed to fetch auth users fallback:', e)
          }
        }

        // If the lead itself has contact details, ensure it's in the list
        const curLead = this.currentLead || {}
        const leadEmail = (curLead.email || '').trim().toLowerCase()
        const leadHasContact = curLead.first_name || curLead.last_name || curLead.email
        if (leadHasContact) {
          const exists = contacts.some(
            (c) => (c.email || '').trim().toLowerCase() === leadEmail && leadEmail !== ''
          )
          if (!exists) {
            contacts.unshift({
              id: 'lead-primary',
              uuid: curLead.uuid || '',
              first_name: curLead.first_name || '',
              last_name: curLead.last_name || '',
              email: curLead.email || '',
              phone_number: curLead.phone_number || '',
              role: 'Primary Contact',
              contact_type: 'lead',
              is_primary: true,
            })
          }
        }

        this.orgContacts = contacts.map((c, idx) => {
          const fullName =
            `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || 'Unnamed Contact'
          let label = fullName
          if (c.email) label += ` (${c.email})`
          if (c.role) label += ` - ${c.role}`
          if (c.contact_type) {
            label += ` [${c.contact_type.toUpperCase()}]`
          } else if (c.is_primary) {
            label += ` [Primary]`
          }
          return {
            ...c,
            key: c.uuid || (c.id ? String(c.id) : c.email || `contact-${idx}`),
            displayName: label,
            fullName,
          }
        })

        // If current contact email matches one of the org contacts, pre-select it
        if (this.form.recipient_email) {
          const curEmail = this.form.recipient_email.trim().toLowerCase()
          const matchedContact = this.orgContacts.find(
            (c) => (c.email || '').trim().toLowerCase() === curEmail
          )
          if (matchedContact) {
            this.selectedContact = matchedContact
          }
        }
      } catch (err) {
        console.error('Failed to load contacts for organisation:', err)
      } finally {
        this.isLoadingContacts = false
      }
    },

    getContactTypeBadgeClass(type) {
      const t = (type || '').toLowerCase()
      switch (t) {
        case 'lead':
          return 'contact-type-badge-lead'
        case 'primary':
          return 'contact-type-badge-primary'
        case 'technical':
          return 'contact-type-badge-technical'
        case 'billing':
        case 'finance':
          return 'contact-type-badge-billing'
        case 'signer':
        case 'signatory':
          return 'contact-type-badge-signer'
        case 'user':
          return 'contact-type-badge-user'
        default:
          return 'contact-type-badge-default'
      }
    },

    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.validationErrors[field] = ''
      }
    },

    validate() {
      let isValid = true
      this.validationErrors = { firstName: '', lastName: '', email: '' }

      const firstName = (this.form.recipient_first_name || '').trim()
      const lastName = (this.form.recipient_last_name || '').trim()
      const email = (this.form.recipient_email || '').trim()

      if (!firstName) {
        this.validationErrors.firstName = 'First Name is required.'
        isValid = false
      }

      if (!lastName) {
        this.validationErrors.lastName = 'Last Name is required.'
        isValid = false
      }

      if (!email) {
        this.validationErrors.email = 'Email address is required.'
        isValid = false
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
          this.validationErrors.email = 'Please enter a valid email address.'
          isValid = false
        }
      }

      return isValid
    },

    async submitForm() {
      if (this.isSubmitting) return

      if (!this.validate()) {
        this.$toast.warning('Please review and correct the required fields.')
        return
      }

      this.isSubmitting = true

      try {
        const leadUuid =
          (this.currentLead && (this.currentLead.uuid || this.currentLead.id)) ||
          (this.currentProposal && (this.currentProposal.lead_id || this.currentProposal.lead_uuid)) ||
          ''

        const contactUuid =
          (!this.isManualRecipient &&
            this.selectedContact &&
            (this.selectedContact.uuid ||
              (this.selectedContact.id !== 'lead-primary' ? String(this.selectedContact.id) : ''))) ||
          ''

        const payload = {
          lead_id: leadUuid,
          contact_uuid: contactUuid,
          recipient_first_name: (this.form.recipient_first_name || '').trim(),
          recipient_last_name: (this.form.recipient_last_name || '').trim(),
          recipient_email: (this.form.recipient_email || '').trim(),
          recipient_phone_number: (this.form.recipient_phone_number || '').trim(),
        }

        const res = await this.$axios.put(`/proposals/${this.proposalId}/lead-contact`, payload)

        this.$toast.success('Proposal recipient contact details updated successfully.')
        this.$emit('updated', res.data.vertex_proposal)
        this.closeModal()
      } catch (err) {
        console.error('Failed to update proposal lead contact:', err)
        const msg =
          (err.response && err.response.data && err.response.data.message) ||
          err.message ||
          'Failed to update proposal recipient contact details.'
        this.$toast.error(msg)
      } finally {
        this.isSubmitting = false
      }
    },

    closeModal() {
      if (this.isSubmitting) return
      this.$emit('close')
    },

    resetState() {
      this.isSubmitting = false
      this.isManualRecipient = false
      this.selectedContact = null
      this.validationErrors = { firstName: '', lastName: '', email: '' }
    },
  },
}
</script>

<style scoped>
.edit-lead-contact-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
  padding: 1rem;
}

.edit-lead-contact-modal {
  position: relative;
  background-color: #ffffff;
  border-radius: 14px;
  width: 100%;
  max-width: 580px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 45px -10px rgba(0, 0, 0, 0.25);
  overflow: visible;
  border: 1px solid #e2e8f0;
}

.edit-lead-contact-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 14px 14px 0 0;
}

.header-icon-badge {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background-color: rgba(0, 160, 193, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}

.edit-lead-contact-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: #1e293b;
}

.btn-close-modal {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.15rem;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  line-height: 1;
  transition: all 0.15s ease;
}

.btn-close-modal:hover:not(:disabled) {
  color: #0f172a;
  background: #e2e8f0;
}

.edit-lead-contact-body {
  padding: 18px 20px;
  overflow: visible;
  flex: 1 1 auto;
}

.edit-lead-contact-body .form-label {
  margin-bottom: 0.5rem !important;
  display: inline-flex;
}

.edit-lead-contact-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  border-radius: 0 0 14px 14px;
}

.btn-vertex-primary {
  background-color: #00A0C1;
  border-color: #00A0C1;
  color: #ffffff;
  transition: all 0.2s ease-in-out;
}

.btn-vertex-primary:hover:not(:disabled) {
  background-color: #0088a4;
  border-color: #0088a4;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}

.btn-vertex-primary:disabled {
  background-color: #00A0C1;
  border-color: #00A0C1;
  color: #ffffff;
  opacity: 0.65;
}

/* Vertex styled checkbox */
.form-check-input:checked {
  background-color: #00A0C1;
  border-color: #00A0C1;
}

.form-check-input:focus {
  border-color: #00A0C1;
  box-shadow: 0 0 0 0.2rem rgba(0, 160, 193, 0.2);
}

/* Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* Contact badges matching VertexProposal standards */
.contact-badge-item {
  display: inline-flex;
  align-items: center;
  line-height: normal;
  vertical-align: middle;
}

.contact-type-badge-lead {
  background-color: rgba(14, 165, 233, 0.12) !important;
  color: #0284c7 !important;
  border: 1px solid rgba(14, 165, 233, 0.3) !important;
}

.contact-type-badge-primary {
  background-color: rgba(16, 185, 129, 0.12) !important;
  color: #059669 !important;
  border: 1px solid rgba(16, 185, 129, 0.3) !important;
}

.contact-type-badge-technical {
  background-color: rgba(124, 58, 237, 0.12) !important;
  color: #7c3aed !important;
  border: 1px solid rgba(124, 58, 237, 0.3) !important;
}

.contact-type-badge-billing {
  background-color: rgba(217, 119, 6, 0.12) !important;
  color: #b45309 !important;
  border: 1px solid rgba(217, 119, 6, 0.3) !important;
}

.contact-type-badge-signer {
  background-color: rgba(225, 29, 72, 0.12) !important;
  color: #e11d48 !important;
  border: 1px solid rgba(225, 29, 72, 0.3) !important;
}

.contact-type-badge-user {
  background-color: rgba(100, 116, 139, 0.12) !important;
  color: #475569 !important;
  border: 1px solid rgba(100, 116, 139, 0.3) !important;
}

.contact-type-badge-default {
  background-color: rgba(107, 114, 128, 0.1) !important;
  color: #4b5563 !important;
  border: 1px solid rgba(107, 114, 128, 0.25) !important;
}

::v-deep(.contact-multiselect .multiselect__option--highlight) {
  background: #00A0C1 !important;
  color: #ffffff !important;
}

::v-deep(.contact-multiselect .multiselect__option--highlight *) {
  color: #ffffff !important;
}

::v-deep(.contact-multiselect .multiselect__option--highlight .contact-option-name),
::v-deep(.contact-multiselect .multiselect__option--highlight .contact-option-sub),
::v-deep(.contact-multiselect .multiselect__option--highlight .text-muted) {
  color: #ffffff !important;
}

::v-deep(.contact-multiselect .multiselect__option--highlight .contact-badge-item) {
  background-color: rgba(255, 255, 255, 0.2) !important;
  color: #ffffff !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}
</style>
